import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import SimulationResultsPanel from './components/SimulationResultsPanel';
import { ensureAnalyticsFromHistory, computeExamAnalytics } from './utils/examAnalytics';
import config from './config';

function ExamHistory({ onBack, darkMode = false, onResumeExam }) {
const [history, setHistory] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [selectedExams, setSelectedExams] = useState([]);
  const [expandedExamId, setExpandedExamId] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusVariant, setStatusVariant] = useState('info');
  const [recalculatingId, setRecalculatingId] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(null);
  const [resumingId, setResumingId] = useState(null);
  const fileInputRef = useRef(null);
  const statusTimeoutRef = useRef(null);

  // Load exam list from MySQL
  useEffect(() => {
    const apiUrl = config.getApiUrl();
    fetch(`${apiUrl}/exam-sessions`)
      .then((res) => res.json())
      .then((payload) => {
        const sessions = Array.isArray(payload?.data) ? payload.data : [];
        const mapped = sessions.map((s) => {
          const dateSource = s.completed_at || s.created_at;
          return {
            id: s.id,
            examTitle: s.exam_title,
            examType: s.exam_type,
            status: s.status || 'completed',
            currentQuestion: s.current_question || 0,
            date: dateSource ? new Date(dateSource).toLocaleDateString() : '',
            time: dateSource ? new Date(dateSource).toLocaleTimeString() : '',
            totalQuestions: s.total_questions,
            scorePercentage: parseFloat(s.accuracy),
            summary: {
              totalQuestions: s.total_questions,
              correct: s.correct,
              incorrect: s.incorrect,
              skipped: s.skipped,
              accuracy: parseFloat(s.accuracy),
            },
            meta: {
              timeSpentSeconds: s.time_spent_seconds,
              allocatedSeconds: s.time_allocated_seconds,
            },
            removed: false,
            _source: 'mysql',
          };
        });
        setHistory(mapped);
      })
      .catch((err) => {
        console.error('[ExamHistory] Error cargando desde MySQL:', err);
        // Fallback to localStorage
        try {
          const stored = localStorage.getItem('examHistory');
          if (stored) setHistory(JSON.parse(stored));
        } catch (_) {
          setHistory([]);
        }
      });
  }, []);

  useEffect(() => {
    return () => {
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, []);

  const activeExams = useMemo(() => history.filter((exam) => !exam.removed), [history]);
  const deletedExams = useMemo(() => history.filter((exam) => exam.removed), [history]);
  const examsToShow = showDeleted ? deletedExams : activeExams;

  const allSelectedThisView =
    examsToShow.length > 0 && examsToShow.every((exam) => selectedExams.includes(exam.id));

  // Resume an in-progress exam from MySQL
  const handleResumeExam = async (examId) => {
    if (!onResumeExam) return;
    setResumingId(examId);
    try {
      const apiUrl = config.getApiUrl();
      const res = await fetch(`${apiUrl}/exam-sessions/${examId}/resume`);
      const data = await res.json();
      if (!data.questions || data.questions.length === 0) {
        alert('No se pudieron cargar las preguntas del examen.');
        setResumingId(null);
        return;
      }

      // Reconstruct quiz state from MySQL data
      const { session, answers, questions } = data;

      // Build userAnswers array: for each question position, find the selected option ID
      const userAnswers = questions.map((q, i) => {
        const answer = answers[i];
        if (!answer || answer.status === 'skipped' || !answer.user_answer_label) return null;
        const matchedOpt = (q.options || []).find(
          opt => opt.option_label && opt.option_label.toLowerCase() === answer.user_answer_label.toLowerCase()
        );
        return matchedOpt ? matchedOpt.id : null;
      });

      // Build questionNotes: { index: { text, confidence } }
      const questionNotes = {};
      answers.forEach((a, i) => {
        if (a.note || a.confidence != null) {
          questionNotes[i] = {
            text: a.note || '',
            confidence: a.confidence ?? null,
          };
        }
      });

      // Build markedForReview: { index: true }
      const markedForReview = {};
      answers.forEach((a, i) => {
        if (a.flagged) markedForReview[i] = true;
      });

      // Build questionTimes: { index: seconds }
      const questionTimes = {};
      answers.forEach((a, i) => {
        if (a.time_spent_seconds) questionTimes[i] = a.time_spent_seconds;
      });

      // Count correct/incorrect
      const correctCount = answers.filter(a => a.status === 'correct').length;
      const incorrectCount = answers.filter(a => a.status === 'incorrect').length;

      onResumeExam({
        questions,
        title: session.exam_title || 'Examen sin Título',
        simulacroId: session.simulacro_id || null,
        sessionId: session.id,
        currentQuestion: session.current_question || 0,
        timeAllocated: session.time_allocated_seconds || null,
        timeSpent: session.time_spent_seconds || 0,
        userAnswers,
        questionNotes,
        markedForReview,
        questionTimes,
        correctAnswersCount: correctCount,
        incorrectAnswersCount: incorrectCount,
      });
    } catch (err) {
      console.error('[ExamHistory] Error reanudando examen:', err);
      alert('Error al cargar el examen para reanudar.');
    }
    setResumingId(null);
  };

  const toggleShowDeleted = () => {
    setShowDeleted((prev) => !prev);
    setSelectedExams([]);
    setExpandedExamId(null);
  };

  const persistHistory = (nextHistory) => {
    setHistory(nextHistory);
    try {
      localStorage.setItem('examHistory', JSON.stringify(nextHistory));
    } catch (err) {
      console.error('[ExamHistory] Error guardando en localStorage:', err);
      showStatus('No se pudo guardar: almacenamiento lleno. Elimina simulacros antiguos.', 'error');
    }
  };

  const updateExam = (examId, updater) => {
    const next = history.map((exam) => (exam.id === examId ? updater(exam) : exam));
    persistHistory(next);
  };

  const handleCheckboxChange = (examId) => {
    setSelectedExams((prev) =>
      prev.includes(examId) ? prev.filter((id) => id !== examId) : [...prev, examId]
    );
  };

  const handleSelectAll = () => {
    if (allSelectedThisView) {
      setSelectedExams([]);
    } else {
      setSelectedExams(examsToShow.map((exam) => exam.id));
    }
  };

  const bulkUpdate = (updater) => {
    const next = history.map((exam) =>
      selectedExams.includes(exam.id) ? updater(exam) : exam
    );
    persistHistory(next);
    setSelectedExams([]);
  };

  const removeSelectedExams = () => bulkUpdate((exam) => ({ ...exam, removed: true }));
  const restoreSelectedExams = () => bulkUpdate((exam) => ({ ...exam, removed: false }));

  const permanentlyDeleteSelected = async () => {
    const apiUrl = config.getApiUrl();
    // Delete from MySQL
    for (const examId of selectedExams) {
      const exam = history.find((e) => e.id === examId);
      if (exam?._source === 'mysql') {
        try {
          await fetch(`${apiUrl}/exam-sessions/${examId}`, { method: 'DELETE' });
        } catch (err) {
          console.error(`Error eliminando session ${examId}:`, err);
        }
      }
    }
    const next = history.filter((exam) => !selectedExams.includes(exam.id));
    setHistory(next);
    setSelectedExams([]);
    if (selectedExams.includes(expandedExamId)) {
      setExpandedExamId(null);
    }
    showStatus(`${selectedExams.length} simulacro(s) eliminado(s).`);
  };

  const handleClearHistory = async () => {
    const apiUrl = config.getApiUrl();
    for (const exam of history) {
      if (exam._source === 'mysql') {
        try {
          await fetch(`${apiUrl}/exam-sessions/${exam.id}`, { method: 'DELETE' });
        } catch (_) {}
      }
    }
    localStorage.removeItem('examHistory');
    setHistory([]);
    setSelectedExams([]);
    setExpandedExamId(null);
    showStatus('Historial limpiado.');
  };

  const showStatus = (message, variant = 'success') => {
    setStatusMessage(message);
    setStatusVariant(variant);
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
    }
    statusTimeoutRef.current = setTimeout(() => setStatusMessage(null), 5000);
  };

  // Load full detail from MySQL when expanding a session
  const loadExamDetail = async (examId) => {
    const exam = history.find((e) => e.id === examId);
    if (!exam || exam._source !== 'mysql') {
      // Not a MySQL exam or already has full data, just toggle
      setExpandedExamId((prev) => (prev === examId ? null : examId));
      return;
    }
    if (exam._detailLoaded) {
      setExpandedExamId((prev) => (prev === examId ? null : examId));
      return;
    }

    setLoadingDetail(examId);
    try {
      const apiUrl = config.getApiUrl();
      const res = await fetch(`${apiUrl}/exam-sessions/${examId}`);
      if (!res.ok) throw new Error('No se pudo cargar el detalle');
      const { answers } = await res.json();

      // Convert MySQL answers to questionDetails format for SimulationResultsPanel
      const questionDetails = (answers || []).map((a) => ({
        index: a.question_index,
        area: a.area || 'Área sin clasificar',
        topic: a.topic || 'Tema sin clasificar',
        subtopic: a.subtopic || 'Subtema sin clasificar',
        subtopicSpecific: a.subtopic_specific || 'Detalle sin clasificar',
        organ: a.organsystem || 'Sistema sin clasificar',
        status: a.status,
        flagged: !!a.flagged,
        userAnswerLabel: a.user_answer_label,
        userAnswerText: a.user_answer_text,
        correctOptionLabel: a.correct_answer_label,
        correctOptionText: a.correct_answer_text,
        question: {
          id: a.question_id,
          question_text: a.question_text,
          case_description: a.case_description,
          area: a.area,
          topic: a.topic,
          subtopic: a.subtopic,
          subtopic_specific: a.subtopic_specific,
          organsystem: a.organsystem,
          especialidad: a.especialidad,
          year: a.year,
          explanation: a.explanation || '',
          options: [],
        },
      }));

      const analytics = computeExamAnalytics({ questionDetails });

      // Update the exam in history with full detail
      setHistory((prev) =>
        prev.map((e) =>
          e.id === examId
            ? { ...e, questionDetails, analytics, _detailLoaded: true }
            : e
        )
      );
      setExpandedExamId(examId);
    } catch (err) {
      console.error('[ExamHistory] Error cargando detalle:', err);
      showStatus('Error cargando detalle del simulacro.', 'error');
    } finally {
      setLoadingDetail(null);
    }
  };

  const getExamsForExport = () => {
    if (selectedExams.length > 0) {
      return history.filter((exam) => selectedExams.includes(exam.id));
    }
    return history;
  };

  const handleExportExams = () => {
    const exams = getExamsForExport();
    if (!exams.length) {
      showStatus('No hay simulacros para exportar.', 'error');
      return;
    }

    const sanitized = exams.map((exam) => {
      const analytics = ensureAnalyticsFromHistory(exam);
      return {
        ...exam,
        analytics,
        summary: analytics?.summary || exam.summary || {
          totalQuestions: 0,
          correct: 0,
          incorrect: 0,
          skipped: 0,
          accuracy: 0,
        },
        questionDetails: analytics?.questionSummaries || exam.questionDetails || [],
      };
    });

    const payload = {
      version: 1,
      exportedAt: new Date().toISOString(),
      count: sanitized.length,
      exams: sanitized,
    };

    try {
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mediexam_history_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showStatus(`Exportados ${sanitized.length} simulacros.`, 'success');
    } catch (error) {
      console.error('Error exportando simulacros', error);
      showStatus('No se pudo exportar el archivo.', 'error');
    }
  };

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const importedExams = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.exams)
        ? parsed.exams
        : [];

      if (!importedExams.length) {
        throw new Error('Formato inválido: sin simulacros.');
      }

      const baseTimestamp = Date.now();
      const existingIds = new Set(history.map((exam) => exam.id));
      const sanitized = importedExams.map((exam, index) => {
        const analytics = ensureAnalyticsFromHistory(exam);
        let newId = exam.id ?? baseTimestamp + index;
        while (existingIds.has(newId)) {
          newId = `${baseTimestamp + index}-${Math.random().toString(36).slice(2, 10)}`;
        }
        existingIds.add(newId);

        return {
          ...exam,
          id: newId,
          analytics,
          summary: analytics?.summary || exam.summary || {
            totalQuestions: 0,
            correct: 0,
            incorrect: 0,
            skipped: 0,
            accuracy: 0,
          },
          questionDetails: analytics?.questionSummaries || exam.questionDetails || [],
          removed: Boolean(exam.removed),
          createdAt: exam.createdAt || baseTimestamp,
        };
      });

      const merged = [...sanitized, ...history];
      persistHistory(merged);
      setShowDeleted(false);
      setSelectedExams([]);
      setExpandedExamId(sanitized[0]?.id ?? null);
      showStatus(`Importados ${sanitized.length} simulacros.`, 'success');
    } catch (error) {
      console.error('Error importando simulacros', error);
      showStatus('No se pudo importar el archivo. Verifica el formato JSON.', 'error');
    } finally {
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const recalculateExam = async (examId) => {
    if (recalculatingId) return;
    const exam = history.find((item) => item.id === examId);
    if (!exam) return;

    const baseDetails = exam.questionDetails || exam.analytics?.questionSummaries || [];
    if (!baseDetails.length) {
      showStatus('Este simulacro no tiene registros suficientes para recalcular.', 'error');
      return;
    }

    setRecalculatingId(examId);

    try {
      // ── PASO 1: Intentar match por ID ──
      let latestByIdMap = {};
      const questionIds = Array.from(
        new Set(
          baseDetails
            .map((detail) => detail.question?.id)
            .filter((id) => Number.isInteger(id) && id > 0)
        )
      );

      if (questionIds.length > 0) {
        const response = await fetch(`${config.getApiUrl()}/questions/by-ids`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids: questionIds }),
        });

        if (response.ok) {
          const payload = await response.json();
          const data = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
          latestByIdMap = data.reduce((acc, question) => {
            if (question?.id) acc[question.id] = question;
            return acc;
          }, {});
        }
      }

      // ── PASO 2: Fallback por texto para las que no se encontraron por ID ──
      const unmatchedDetails = baseDetails.filter((detail) => {
        const qId = detail.question?.id;
        return !qId || !latestByIdMap[qId];
      });

      let latestByTextMap = {};
      if (unmatchedDetails.length > 0) {
        const textQueries = unmatchedDetails
          .filter((d) => d.question?.question_text)
          .map((d) => ({
            question_text: d.question.question_text,
            case_description: d.question.case_description || '',
          }));

        if (textQueries.length > 0) {
          console.log(`[recalculate] Enviando ${textQueries.length} queries por texto...`);
          const textResponse = await fetch(`${config.getApiUrl()}/questions/by-text`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ queries: textQueries }),
          });

          console.log(`[recalculate] by-text response status: ${textResponse.status}`);
          if (textResponse.ok) {
            const textPayload = await textResponse.json();
            const textData = Array.isArray(textPayload) ? textPayload : Array.isArray(textPayload?.data) ? textPayload.data : [];
            console.log(`[recalculate] by-text devolvió ${textData.length} preguntas`);
            // Index by question_text for lookup
            for (const q of textData) {
              if (q?.question_text) {
                const key = (q.case_description || '') + '||' + q.question_text;
                latestByTextMap[key] = q;
              }
            }
          } else {
            const errBody = await textResponse.text().catch(() => '');
            console.error(`[recalculate] by-text error: ${textResponse.status}`, errBody);
          }
        }
      }

      // ── PASO 3: Merge — solo actualizar clasificación, NO tocar opciones/scoring ──
      let matchedById = 0;
      let matchedByText = 0;
      let unmatched = 0;

      const CLASSIFICATION_FIELDS = [
        'area', 'topic', 'subtopic', 'subtopic_specific',
        'especialidad', 'organsystem', 'year',
        'explanation', 'key_points', 'ascii_diagram',
      ];

      const updatedDetails = baseDetails.map((detail) => {
        const originalQuestion = detail.question || {};
        let latestQuestion = null;

        // Intento 1: Match por ID
        if (originalQuestion.id && latestByIdMap[originalQuestion.id]) {
          latestQuestion = latestByIdMap[originalQuestion.id];
          matchedById++;
        }

        // Intento 2: Match por texto
        if (!latestQuestion && originalQuestion.question_text) {
          const key = (originalQuestion.case_description || '') + '||' + originalQuestion.question_text;
          if (latestByTextMap[key]) {
            latestQuestion = latestByTextMap[key];
            matchedByText++;
          }
        }

        if (!latestQuestion) {
          unmatched++;
          return detail;
        }

        // Solo actualizar campos de clasificación, mantener opciones y scoring original
        const mergedQuestion = { ...originalQuestion };
        for (const field of CLASSIFICATION_FIELDS) {
          if (latestQuestion[field] !== undefined) {
            mergedQuestion[field] = latestQuestion[field];
          }
        }

        // Actualizar también los campos de nivel superior del detail
        return {
          ...detail,
          area: mergedQuestion.area || detail.area,
          topic: mergedQuestion.topic || detail.topic,
          subtopic: mergedQuestion.subtopic || detail.subtopic,
          subtopicSpecific: mergedQuestion.subtopic_specific || detail.subtopicSpecific,
          organ: mergedQuestion.organsystem || detail.organ,
          question: mergedQuestion,
        };
      });

      if (matchedById === 0 && matchedByText === 0) {
        showStatus('No se pudieron recuperar las preguntas actualizadas. Revisa que existan en la base.', 'error');
        return;
      }

      const newAnalytics = computeExamAnalytics({ questionDetails: updatedDetails });
      if (!newAnalytics || !newAnalytics.summary) {
        showStatus('No fue posible recalcular la analítica.', 'error');
        return;
      }

      const updatedExam = {
        ...exam,
        analytics: newAnalytics,
        summary: newAnalytics.summary,
        questionDetails: newAnalytics.questionSummaries,
        scorePercentage: newAnalytics.summary.accuracy,
        meta: {
          ...exam.meta,
          recalculatedAt: new Date().toISOString(),
        },
      };

      updateExam(exam.id, () => updatedExam);
      setExpandedExamId(exam.id);

      const parts = [];
      if (matchedById > 0) parts.push(`${matchedById} por ID`);
      if (matchedByText > 0) parts.push(`${matchedByText} por texto`);
      if (unmatched > 0) parts.push(`${unmatched} sin cambios`);
      showStatus(`Simulacro recalculado (${parts.join(', ')}).`, 'success');
    } catch (error) {
      console.error('Error recalculando simulacro', error);
      showStatus(error.message || 'Error al recalcular el simulacro.', 'error');
    } finally {
      setRecalculatingId(null);
    }
  };

  const containerClasses = darkMode
    ? 'min-h-screen bg-slate-950 py-8 text-slate-100'
    : 'min-h-screen bg-gray-100 py-8 text-slate-800';

  const cardClasses = darkMode
    ? 'bg-slate-900/80 border border-slate-800 text-slate-100'
    : 'bg-white border border-slate-200 text-slate-800';

  return (
    <div className={containerClasses}>
      <motion.div
        className={`max-w-6xl mx-auto p-6 rounded-3xl shadow-xl ${cardClasses}`}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <h2 className="text-2xl font-bold">Historial de Simulacros</h2>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={toggleShowDeleted}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                showDeleted
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              >
              {showDeleted ? 'Ver activos' : 'Ver borrados'}
            </button>
            <button
              onClick={onBack}
              className={`${
                darkMode
                  ? 'bg-slate-800 hover:bg-slate-700 text-slate-100'
                  : 'bg-gray-400 hover:bg-gray-500 text-white'
              } px-4 py-2 rounded-lg font-semibold`}
            >
              Volver
            </button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleExportExams}
              className={`px-4 py-2 rounded-lg font-semibold ${
                darkMode ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-emerald-500 text-white hover:bg-emerald-600'
              }`}
            >
              Exportar JSON
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleImportClick}
              className={`px-4 py-2 rounded-lg font-semibold ${
                darkMode ? 'bg-sky-600 text-white hover:bg-sky-700' : 'bg-sky-500 text-white hover:bg-sky-600'
              }`}
            >
              Importar JSON
            </motion.button>
          </div>
        </div>

        {statusMessage && (
          <div
            className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
              statusVariant === 'error'
                ? darkMode
                  ? 'border-rose-500/40 bg-rose-500/10 text-rose-200'
                  : 'border-rose-200 bg-rose-50 text-rose-600'
                : darkMode
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                : 'border-emerald-200 bg-emerald-50 text-emerald-600'
            }`}
          >
            {statusMessage}
          </div>
        )}

        <div className="flex flex-wrap gap-2 justify-between items-center mb-4">
          <div className="flex gap-2">
            {!showDeleted ? (
              <>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  disabled={selectedExams.length === 0}
                  onClick={removeSelectedExams}
                  className={`px-4 py-2 rounded-lg font-semibold transition ${
                    selectedExams.length === 0
                      ? 'bg-slate-600/40 text-slate-400 cursor-not-allowed'
                      : 'bg-rose-500 text-white hover:bg-rose-600'
                  }`}
                >
                  Quitar seleccionados
                </motion.button>
              </>
            ) : (
              <div className="flex gap-2">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  disabled={selectedExams.length === 0}
                  onClick={restoreSelectedExams}
                  className={`px-4 py-2 rounded-lg font-semibold transition ${
                    selectedExams.length === 0
                      ? 'bg-slate-600/40 text-slate-400 cursor-not-allowed'
                      : 'bg-emerald-500 text-white hover:bg-emerald-600'
                  }`}
                >
                  Restaurar seleccionados
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  disabled={selectedExams.length === 0}
                  onClick={permanentlyDeleteSelected}
                  className={`px-4 py-2 rounded-lg font-semibold transition ${
                    selectedExams.length === 0
                      ? 'bg-slate-600/40 text-slate-400 cursor-not-allowed'
                      : 'bg-rose-600 text-white hover:bg-rose-700'
                  }`}
                >
                  Borrar definitivo
                </motion.button>
              </div>
            )}
          </div>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleClearHistory}
            className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 font-semibold"
          >
            Borrar todo
          </motion.button>
        </div>

        {examsToShow.length === 0 ? (
          <p className={darkMode ? 'text-slate-400 text-center' : 'text-slate-600 text-center'}>
            {showDeleted ? 'No hay simulacros borrados.' : 'No hay simulacros en el historial.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead className={darkMode ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-600'}>
                <tr>
                  <th className="border px-2 py-2 text-center">
                    <input type="checkbox" checked={allSelectedThisView} onChange={handleSelectAll} />
                  </th>
                  <th className="border px-3 py-2 text-left">Título</th>
                  <th className="border px-3 py-2 text-left">Fecha</th>
                  <th className="border px-3 py-2 text-left">Hora</th>
                  <th className="border px-3 py-2 text-center">Preguntas</th>
                  <th className="border px-3 py-2 text-center">Puntaje</th>
                  <th className="border px-3 py-2 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {examsToShow.map((exam) => {
                  const analytics = ensureAnalyticsFromHistory(exam);
                  const summary = analytics?.summary || {};
                  const isExpanded = expandedExamId === exam.id;
                  const meta = exam.meta || {};

                  return (
                    <React.Fragment key={exam.id}>
                      <tr className={darkMode ? 'odd:bg-slate-900/50' : 'odd:bg-white'}>
                        <td className="border px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={selectedExams.includes(exam.id)}
                            onChange={() => handleCheckboxChange(exam.id)}
                          />
                        </td>
                        <td className="border px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span>{exam.examTitle || 'Simulacro'}</span>
                            {exam.status === 'in_progress' && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                darkMode ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-amber-100 text-amber-700 border border-amber-300'
                              }`}>
                                En progreso ({exam.currentQuestion}/{exam.totalQuestions})
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="border px-3 py-2">{exam.date}</td>
                        <td className="border px-3 py-2">{exam.time}</td>
                        <td className="border px-3 py-2 text-center">{summary.totalQuestions || exam.totalQuestions || '-'}</td>
                        <td className="border px-3 py-2 text-center">{Math.round(summary.accuracy || exam.scorePercentage || 0)}%</td>
                        <td className="border px-3 py-2 text-center space-x-2">
                          {exam.status === 'in_progress' && onResumeExam && (
                            <motion.button
                              whileHover={{ scale: resumingId === exam.id ? 1 : 1.04 }}
                              whileTap={{ scale: resumingId === exam.id ? 1 : 0.96 }}
                              disabled={resumingId === exam.id}
                              onClick={() => handleResumeExam(exam.id)}
                              className={`px-3 py-1 rounded text-sm font-semibold ${
                                resumingId === exam.id
                                  ? 'bg-slate-500 text-white cursor-not-allowed'
                                  : 'bg-amber-500 text-white hover:bg-amber-600'
                              }`}
                            >
                              {resumingId === exam.id ? 'Cargando…' : 'Continuar'}
                            </motion.button>
                          )}
                          <motion.button
                            whileHover={{ scale: recalculatingId === exam.id ? 1 : 1.04 }}
                            whileTap={{ scale: recalculatingId === exam.id ? 1 : 0.96 }}
                            disabled={recalculatingId === exam.id}
                            onClick={() => recalculateExam(exam.id)}
                            className={`px-3 py-1 rounded text-sm ${
                              recalculatingId === exam.id
                                ? 'bg-slate-500 text-white cursor-not-allowed'
                                : 'bg-emerald-500 text-white hover:bg-emerald-600'
                            }`}
                          >
                            {recalculatingId === exam.id ? 'Recalculando…' : 'Recalcular'}
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: loadingDetail === exam.id ? 1 : 1.04 }}
                            whileTap={{ scale: loadingDetail === exam.id ? 1 : 0.96 }}
                            disabled={loadingDetail === exam.id}
                            onClick={() => loadExamDetail(exam.id)}
                            className={`px-3 py-1 rounded text-sm ${
                              loadingDetail === exam.id
                                ? 'bg-slate-500 text-white cursor-not-allowed'
                                : 'bg-blue-500 text-white hover:bg-blue-600'
                            }`}
                          >
                            {loadingDetail === exam.id ? 'Cargando…' : isExpanded ? 'Ocultar' : 'Ver detalle'}
                          </motion.button>
                          {!showDeleted ? (
                            <motion.button
                              whileHover={{ scale: 1.04 }}
                              whileTap={{ scale: 0.96 }}
                              className="px-3 py-1 bg-rose-500 text-white rounded hover:bg-rose-600 text-sm"
                              onClick={() => updateExam(exam.id, (ex) => ({ ...ex, removed: true }))}
                            >
                              Quitar
                            </motion.button>
                          ) : (
                            <div className="inline-flex gap-1">
                              <motion.button
                                whileHover={{ scale: 1.04 }}
                                whileTap={{ scale: 0.96 }}
                                className="px-3 py-1 bg-emerald-500 text-white rounded hover:bg-emerald-600 text-sm"
                                onClick={() => updateExam(exam.id, (ex) => ({ ...ex, removed: false }))}
                              >
                                Restaurar
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.04 }}
                                whileTap={{ scale: 0.96 }}
                                className="px-3 py-1 bg-rose-600 text-white rounded hover:bg-rose-700 text-sm"
                                onClick={() => {
                                  persistHistory(history.filter((item) => item.id !== exam.id));
                                  setSelectedExams((prev) => prev.filter((id) => id !== exam.id));
                                  if (expandedExamId === exam.id) setExpandedExamId(null);
                                }}
                              >
                                Del
                              </motion.button>
                            </div>
                          )}
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td colSpan={7} className={`border px-3 py-4 ${darkMode ? 'bg-slate-950/70' : 'bg-slate-50'}`}>
                            <SimulationResultsPanel
                              darkMode={darkMode}
                              analytics={analytics}
                              timeSpentSeconds={meta.timeSpentSeconds}
                              allocatedSeconds={meta.allocatedSeconds}
                              headerMeta={{
                                title: exam.examTitle,
                                date: exam.date,
                                time: exam.time,
                              }}
                              subscription={{ isAdmin: false }}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
      <input
        type="file"
        accept="application/json"
        ref={fileInputRef}
        className="hidden"
        onChange={handleImportFile}
      />
    </div>
  );
}

export default ExamHistory;
